import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Tabs,
  Tab,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  Assessment,
  PlayArrow,
  Add,
  Schedule,
  CheckCircle,
  Visibility,
  Delete,
  FilterList,
} from '@mui/icons-material';
import { auditAPI, companyAPI, Company, AuditSession } from '../services/api';

interface DeleteInfo {
  session_id: number;
  audit_name: string;
  company_name: string;
  site_name: string;
  start_date: string;
  delete_counts: {
    action_items: number;
    subtasks: number;
    task_comments: number;
    subtask_comments: number;
    answers: number;
    analyses: number;
    commentary: number;
    kpi_links: number;
  };
}

const AuditManagement: React.FC = () => {
  const { companyId } = useParams<{ companyId: string }>();
  const navigate = useNavigate();
  const [company, setCompany] = useState<Company | null>(null);
  const [auditSessions, setAuditSessions] = useState<AuditSession[]>([]);
  const [auditSeriesLookup, setAuditSeriesLookup] = useState<Record<number, string>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [searchFilter, setSearchFilter] = useState('');
  const [siteFilter, setSiteFilter] = useState<string>('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [auditToDelete, setAuditToDelete] = useState<AuditSession | null>(null);
  const [deleteInfo, setDeleteInfo] = useState<DeleteInfo | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [loadingDeleteInfo, setLoadingDeleteInfo] = useState(false);

  useEffect(() => {
    const loadData = async () => {
      if (!companyId) return;
      
      try {
        setLoading(true);
        
        const [companyData, sessionsData, auditSeriesData] = await Promise.all([
          companyAPI.getCompany(parseInt(companyId)),
          auditAPI.getAuditSessions(parseInt(companyId)),
          auditAPI.getAuditSeries()
        ]);
        
        setCompany(companyData);
        setAuditSessions(sessionsData);
        
        // Create lookup for audit series names
        const seriesLookup: Record<number, string> = {};
        auditSeriesData.forEach((series: any) => {
          seriesLookup[series.id] = series.name;
        });
        setAuditSeriesLookup(seriesLookup);
        
        console.log('✅ Audit management data loaded:', { companyData, sessionsData });
        
      } catch (error) {
        console.error('Failed to load audit management data:', error);
        setError('Failed to load review data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [companyId]);

  // Get unique sites from all sessions for filter dropdown
  // NOTE: This must be before any early returns to follow Rules of Hooks
  const uniqueSites = useMemo(() => {
    const sites = new Set<string>();
    auditSessions.forEach(session => {
      if (session.site_name) {
        sites.add(session.site_name);
      }
    });
    return Array.from(sites).sort();
  }, [auditSessions]);

  const handleStartNewAudit = () => {
    navigate(`/company/${companyId}/audit/start`);
  };

  const handleContinueAudit = (sessionId: number) => {
    navigate(`/audit-session/${sessionId}`);
  };

  const handleViewAuditResults = (sessionId: number) => {
    navigate(`/audit-analysis/${sessionId}`);
  };

  const handleDeleteClick = async (session: AuditSession) => {
    setAuditToDelete(session);
    setDeleteDialogOpen(true);
    
    // Fetch delete info to show user what will be deleted
    try {
      setLoadingDeleteInfo(true);
      const info = await auditAPI.getAuditSessionDeleteInfo(session.id);
      setDeleteInfo(info);
    } catch (error) {
      console.error('Error fetching delete info:', error);
      // Continue anyway - deletion will still work
      setDeleteInfo(null);
    } finally {
      setLoadingDeleteInfo(false);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!auditToDelete) return;
    
    try {
      setDeleting(true);
      await auditAPI.deleteAuditSession(auditToDelete.id);
      
      // Remove from local state after successful API call
      setAuditSessions(prev => prev.filter(session => session.id !== auditToDelete.id));
      
      console.log('✅ Audit session deleted:', auditToDelete.id);
    } catch (error) {
      console.error('❌ Error deleting audit session:', error);
      setError('Failed to delete review session');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setAuditToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setAuditToDelete(null);
    setDeleteInfo(null);
  };

  const getAuditName = (session: AuditSession) => {
    return auditSeriesLookup[session.audit_series] || `Review Type ${session.audit_series}`;
  };

  const getStatusChip = (session: AuditSession) => {
    if (session.end_date) {
      return <Chip label="Completed" color="success" size="small" />;
    } else {
      return <Chip label="In Progress" color="info" size="small" />;
    }
  };

  const getActionButtons = (session: AuditSession) => {
    return (
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
        {session.end_date ? (
          <Tooltip title="View Results">
            <IconButton 
              color="primary" 
              onClick={() => handleViewAuditResults(session.id)}
              data-testid="view-audit-results-button"
            >
              <Visibility />
            </IconButton>
          </Tooltip>
        ) : (
          <Tooltip title="Continue Review">
            <IconButton 
              color="success" 
              onClick={() => handleContinueAudit(session.id)}
              data-testid="continue-audit-button"
            >
              <PlayArrow />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Delete Review">
          <IconButton 
            color="error" 
            onClick={() => handleDeleteClick(session)}
            data-testid="delete-audit-button"
          >
            <Delete />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading review management...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!company) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error">Company not found</Alert>
      </Container>
    );
  }

  // Filter audits based on search and site filter
  const filteredSessions = auditSessions.filter(session => {
    // Search filter
    if (searchFilter) {
      const auditName = getAuditName(session).toLowerCase();
      if (!auditName.includes(searchFilter.toLowerCase())) {
        return false;
      }
    }
    // Site filter (only apply to completed tab)
    if (activeTab === 1 && siteFilter !== 'all') {
      if (session.site_name !== siteFilter) {
        return false;
      }
    }
    return true;
  });

  const inProgressAudits = filteredSessions.filter(session => !session.end_date);
  const completedAudits = filteredSessions.filter(session => session.end_date);
  
  // Clear filters
  const clearFilters = () => {
    setSearchFilter('');
    setSiteFilter('all');
  };
  
  // Get current tab data
  const getCurrentTabData = () => {
    return activeTab === 0 ? inProgressAudits : completedAudits;
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Assessment fontSize="large" />
            Review Management - {company.name}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Manage ongoing reviews and start new review sessions
          </Typography>
        </Box>
        <Button
          variant="contained"
          size="large"
          startIcon={<Add />}
          onClick={handleStartNewAudit}
          data-testid="start-new-audit-button"
        >
          Start New Review
        </Button>
      </Box>

      {/* Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 3, mb: 4 }}>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="info.main">
              {inProgressAudits.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              In Progress
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="success.main">
              {completedAudits.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Completed
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color="primary.main">
              {auditSessions.length}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Reviews
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Filters and Tabs */}
      <Card sx={{ mb: 4 }}>
        <CardContent>
          {/* Search Filter */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3, flexWrap: 'wrap' }}>
            <FilterList color="action" />
            <TextField
              label="Filter by Review Name"
              variant="outlined"
              size="small"
              value={searchFilter}
              onChange={(e) => setSearchFilter(e.target.value)}
              placeholder="Search reviews..."
              sx={{ minWidth: 300 }}
            />
            
            {/* Site Filter - only show on completed tab */}
            {activeTab === 1 && (
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Site</InputLabel>
                <Select
                  value={siteFilter}
                  label="Site"
                  onChange={(e) => setSiteFilter(e.target.value)}
                >
                  <MenuItem value="all">All Sites</MenuItem>
                  {uniqueSites.map(site => (
                    <MenuItem key={site} value={site}>{site}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            
            {/* Clear Filters */}
            {(searchFilter || siteFilter !== 'all') && (
              <Button
                size="small"
                onClick={clearFilters}
                color="inherit"
              >
                Clear Filters
              </Button>
            )}
          </Box>

          {/* Tabs */}
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} aria-label="review tabs">
            <Tab label={`In Progress (${inProgressAudits.length})`} />
            <Tab label={`Completed (${completedAudits.length})`} />
          </Tabs>
        </CardContent>
      </Card>

            {/* Tabbed Review Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {activeTab === 0 ? (
              <>
                <Schedule color="info" />
                Reviews In Progress ({inProgressAudits.length})
              </>
            ) : (
              <>
                <CheckCircle color="success" />
                Completed Reviews ({completedAudits.length})
              </>
            )}
          </Typography>
          
          {getCurrentTabData().length > 0 ? (
            <TableContainer component={Paper} variant="outlined">
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Review Name</TableCell>
                    <TableCell>Site</TableCell>
                    <TableCell>Started</TableCell>
                    {activeTab === 1 && <TableCell>Completed</TableCell>}
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getCurrentTabData().map((session) => (
                    <TableRow key={session.id} hover>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {getAuditName(session)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {session.site_name || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {new Date(session.start_date).toLocaleDateString()}
                      </TableCell>
                      {activeTab === 1 && (
                        <TableCell>
                          {session.end_date ? new Date(session.end_date).toLocaleDateString() : '-'}
                        </TableCell>
                      )}
                      <TableCell>
                        {getStatusChip(session)}
                      </TableCell>
                      <TableCell align="center">
                        {getActionButtons(session)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="text.secondary">
                {searchFilter ? 
                  `No ${activeTab === 0 ? 'in progress' : 'completed'} reviews found matching "${searchFilter}"` :
                  `No ${activeTab === 0 ? 'in progress' : 'completed'} reviews yet`
                }
              </Typography>
              {!searchFilter && activeTab === 0 && (
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleStartNewAudit}
                  sx={{ mt: 2 }}
                >
                  {auditSessions.length === 0 ? 'Start Your First Review' : 'Start Your Next Review'}
                </Button>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="delete-dialog-title" sx={{ color: 'error.main' }}>
          ⚠️ Delete Review Session
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description" component="div">
            Are you sure you want to delete this review session? This action cannot be undone.
            
            {auditToDelete && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {getAuditName(auditToDelete)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Site: {auditToDelete.site_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Started: {new Date(auditToDelete.start_date).toLocaleDateString()}
                </Typography>
              </Box>
            )}

            {loadingDeleteInfo && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                <Typography variant="body2">Loading delete information...</Typography>
              </Box>
            )}

            {deleteInfo && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" color="error.main" fontWeight="bold" gutterBottom>
                  The following data will be permanently deleted:
                </Typography>
                <Box component="ul" sx={{ mt: 1, pl: 2 }}>
                  {deleteInfo.delete_counts.action_items > 0 && (
                    <Typography component="li" variant="body2">
                      <strong>{deleteInfo.delete_counts.action_items}</strong> action item{deleteInfo.delete_counts.action_items !== 1 ? 's' : ''}
                    </Typography>
                  )}
                  {deleteInfo.delete_counts.subtasks > 0 && (
                    <Typography component="li" variant="body2">
                      <strong>{deleteInfo.delete_counts.subtasks}</strong> subtask{deleteInfo.delete_counts.subtasks !== 1 ? 's' : ''}
                    </Typography>
                  )}
                  {deleteInfo.delete_counts.task_comments > 0 && (
                    <Typography component="li" variant="body2">
                      <strong>{deleteInfo.delete_counts.task_comments}</strong> task comment{deleteInfo.delete_counts.task_comments !== 1 ? 's' : ''}
                    </Typography>
                  )}
                  {deleteInfo.delete_counts.subtask_comments > 0 && (
                    <Typography component="li" variant="body2">
                      <strong>{deleteInfo.delete_counts.subtask_comments}</strong> subtask comment{deleteInfo.delete_counts.subtask_comments !== 1 ? 's' : ''}
                    </Typography>
                  )}
                  {deleteInfo.delete_counts.answers > 0 && (
                    <Typography component="li" variant="body2">
                      <strong>{deleteInfo.delete_counts.answers}</strong> review answer{deleteInfo.delete_counts.answers !== 1 ? 's' : ''}
                    </Typography>
                  )}
                  {deleteInfo.delete_counts.analyses > 0 && (
                    <Typography component="li" variant="body2">
                      <strong>{deleteInfo.delete_counts.analyses}</strong> {deleteInfo.delete_counts.analyses !== 1 ? 'analyses' : 'analysis'}
                    </Typography>
                  )}
                  {deleteInfo.delete_counts.commentary > 0 && (
                    <Typography component="li" variant="body2">
                      <strong>{deleteInfo.delete_counts.commentary}</strong> commentary item{deleteInfo.delete_counts.commentary !== 1 ? 's' : ''}
                    </Typography>
                  )}
                  {deleteInfo.delete_counts.kpi_links > 0 && (
                    <Typography component="li" variant="body2">
                      <strong>{deleteInfo.delete_counts.kpi_links}</strong> KPI link{deleteInfo.delete_counts.kpi_links !== 1 ? 's' : ''}
                    </Typography>
                  )}
                </Box>
                {(deleteInfo.delete_counts.action_items === 0 && 
                  deleteInfo.delete_counts.subtasks === 0 &&
                  deleteInfo.delete_counts.answers === 0) && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    No associated data found for this review.
                  </Typography>
                )}
              </Box>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            variant="contained"
            disabled={deleting || loadingDeleteInfo}
            startIcon={deleting ? <CircularProgress size={16} /> : <Delete />}
          >
            {deleting ? 'Deleting...' : 'Delete Permanently'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AuditManagement; 